<template>
  <div class="ai-summary-card" :class="{ collapsed }" v-if="aiEnabled && (summary || error)">
    <div class="ai-summary-header">
      <div class="ai-summary-title">
        <fa-icon :icon="['fal', 'sparkles']" />
        <span>Resumo do paciente</span>
        <span class="ai-summary-tag">IA</span>
      </div>

      <div class="ai-summary-actions" v-if="!generating">
        <template v-if="expanded">
          <button type="button" class="btn btn-sm btn-link" @click="generate()" v-if="outdated">
            Atualizar
          </button>
          <button type="button" class="btn btn-sm btn-link" @click="expanded = false">
            Ocultar
          </button>
        </template>

        <button type="button" class="btn btn-sm btn-link" @click="open()" v-else>
          {{ content ? 'Ver resumo' : 'Gerar resumo' }}
        </button>
      </div>
    </div>

    <div class="ai-summary-loading" v-if="generating">
      <div class="loading" />
      <span>Gerando resumo dos atendimentos...</span>
    </div>

    <template v-else-if="expanded && content">
      <p class="ai-summary-content">{{ summary.content }}</p>
      <div class="ai-summary-footer">
        <span>
          Gerado por IA a partir de {{ summary.recordsCount }}
          {{ summary.recordsCount === 1 ? 'atendimento' : 'atendimentos' }}.
          Revise antes de utilizar.
        </span>
        <span v-if="summary.generatedAt">
          <template v-if="outdated">Há atendimento mais recente.</template>
          Gerado em {{ summary.generatedAt | date('datetime') }}
        </span>
      </div>

      <div class="ai-summary-privacy">
        Resumo gerado em servidores próprios. Os dados do paciente não são enviados
        a terceiros, em conformidade com a LGPD.
      </div>
    </template>

    <div class="ai-summary-error" v-else-if="expanded && error">
      <span>Não foi possível gerar o resumo do paciente.</span>
      <button type="button" class="btn btn-sm btn-link" @click="retry()">
        Tentar novamente
      </button>
    </div>
  </div>
</template>

<script>
import ai from '@/mixins/ai';

export default {
  mixins: [ai],
  props: ['patientId'],
  data() {
    return {
      expanded: false,
      generating: false,
      error: false,
      summary: null,
    };
  },
  computed: {
    content() {
      return this.summary && this.summary.content;
    },
    collapsed() {
      return !this.generating && (!this.expanded || (!this.content && !this.error));
    },
    pending() {
      return !!this.summary && !this.summary.content;
    },
    outdated() {
      return !!this.summary && !!this.summary.content && !!this.summary.outdated;
    },
  },
  mounted() {
    this.load();
  },
  watch: {
    patientId() {
      this.summary = null;
      this.error = false;
      this.expanded = false;
      this.load();
    },
  },
  methods: {
    retry() {
      this.error = false;

      return this.summary ? this.generate() : this.load();
    },
    open() {
      if (this.content) {
        this.expanded = true;
        return Promise.resolve();
      }

      return this.generate();
    },
    load() {
      if (!this.patientId || !this.aiEnabled) {
        return Promise.resolve();
      }

      return this.$httpX
        .get(`/patients/${this.patientId}/medical-record-summary`)
        .then(({ data }) => {
          this.summary = data || null;
        })
        .catch(() => {
          this.error = true;
          this.expanded = true;
        });
    },
    generate() {
      if (!this.patientId || this.generating) {
        return Promise.resolve();
      }

      this.error = false;
      this.generating = true;

      return this.$httpX
        .post(`/patients/${this.patientId}/medical-record-summary`, {
          refresh: this.outdated,
        })
        .then(({ data }) => {
          if (data && data.content) {
            this.summary = data;
          } else {
            this.error = true;
          }

          this.expanded = true;
        })
        .catch(() => {
          this.error = true;
          this.expanded = true;
        })
        .finally(() => {
          this.generating = false;
        });
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/_variables.scss";

.ai-summary-card {
  background: $yellow-color-100;
  border: $border-width-lg dashed $yellow-color-500;
  border-radius: $border-radius;
  margin-bottom: $layout-spacing-lg;
  padding: $layout-spacing-lg;

  .ai-summary-header {
    align-items: center;
    display: flex;
    justify-content: space-between;
    margin-bottom: $layout-spacing;
  }

  &.collapsed .ai-summary-header {
    margin-bottom: 0;
  }

  .ai-summary-actions {
    display: flex;
    gap: $layout-spacing;

    .btn-link {
      color: $yellow-color-800;
      font-weight: 600;
    }
  }

  .ai-summary-title {
    align-items: center;
    color: $yellow-color-800;
    display: flex;
    font-size: $font-size-lg;
    font-weight: 600;
    gap: $layout-spacing;
  }

  .ai-summary-tag {
    background: $yellow-color-300;
    border-radius: $border-radius;
    color: $yellow-color-900;
    font-size: $font-size-sm;
    font-weight: 600;
    padding: 0 $layout-spacing-sm;
    text-transform: uppercase;
  }

  .ai-summary-loading {
    align-items: center;
    color: $yellow-color-800;
    display: flex;
    gap: $layout-spacing-lg;
    padding: $layout-spacing 0;

    .loading {
      margin: 0;
    }
  }

  .ai-summary-content {
    color: $gray-color-900;
    margin: 0;
    white-space: pre-line;
  }

  .ai-summary-footer {
    color: $yellow-color-800;
    display: flex;
    font-size: $font-size-sm;
    gap: $layout-spacing;
    justify-content: space-between;
    margin-top: $layout-spacing;
  }

  .ai-summary-privacy {
    border-top: $border-width dashed $yellow-color-300;
    color: $yellow-color-800;
    font-size: $font-size-sm;
    margin-top: $layout-spacing;
    padding-top: $layout-spacing;
  }

  .ai-summary-error {
    align-items: center;
    color: $gray-color-800;
    display: flex;
    gap: $layout-spacing;
  }
}
</style>
